import React, { useState } from 'react';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { compact, day, inr, num, qty } from '../lib/format.js';
import { BarsH, StackedBars } from '../components/charts.jsx';
import { Alert, Card, CardSkeleton, Empty, StatCard, useAsync } from '../components/ui.jsx';
import { thisMonth } from '../lib/dates.js';
import PeriodPicker from '../components/PeriodPicker.jsx';
import ExportButton, { periodSlug, periodText } from '../components/ExportButton.jsx';

/** What the business actually made, and what is quietly ageing in the godown. */
export default function Analytics() {
  const session = useSession();
  const entityId = session.entities[0] && session.entities[0].id;
  const [period, setPeriod] = useState(thisMonth);
  const { from, to } = period;
  const [by, setBy] = useState('item');

  const margin = useAsync(() => api.get(`/api/reports/margin?from=${from}&to=${to}&by=${by}&entity_id=${entityId}`), [from, to, by, entityId]);
  const ageing = useAsync(() => api.get(`/api/reports/ageing?entity_id=${entityId}`), [entityId]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Margin and ageing</h1>
          <div className="page-sub">
            The batch carries what it was bought at, so the margin on a sale is knowable line by line — and stock
            that has been sitting too long is visible before it becomes a write-off.
          </div>
        </div>
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <PeriodPicker value={period} onChange={setPeriod} />
          <ExportButton build={async () => {
            const by3 = await Promise.all(['item', 'customer', 'batch'].map((b) => api.get(
              `/api/reports/margin?from=${from}&to=${to}&by=${b}&entity_id=${entityId}&all=true`)));
            const marginCols = (label) => [
              { key: 'label', header: label }, { key: 'qty', header: 'Quantity', type: 'number', total: true },
              { key: 'revenue', header: 'Revenue', type: 'money', total: true },
              { key: 'cost', header: 'Cost', type: 'money', total: true },
              { key: 'margin', header: 'Margin', type: 'money', total: true },
              { key: 'margin_pct', header: 'Margin %', type: 'percent' },
              { key: 'uncosted_revenue', header: 'Uncosted revenue', type: 'money', total: true },
            ];
            const sheets = by3.map((m, i) => ({
              name: ['By item', 'By customer', 'By batch'][i], columns: marginCols(['Item', 'Customer', 'Batch'][i]), rows: m.rows,
            }));
            if (ageing.data) {
              sheets.push({
                name: 'Stock age', title: 'How long stock has been sitting', subtitle: 'At cost, by days since purchase',
                columns: [{ key: 'bucket', header: 'Days since purchase' }, { key: 'qty', header: 'Quantity', type: 'number', total: true },
                  { key: 'value', header: 'Value at cost', type: 'money', total: true }],
                rows: ageing.data.buckets,
              });
              sheets.push({
                name: 'Expiring', title: 'In stock, expiring within sixty days',
                columns: [{ key: 'item_name', header: 'Item' }, { key: 'batch_name', header: 'Batch' },
                  { key: 'godown_name', header: 'Godown' }, { key: 'expiry_date', header: 'Expiry', type: 'date' },
                  { key: 'days_to_expiry', header: 'Days left', type: 'int' },
                  { key: 'qty_available', header: 'In stock', type: 'number', total: true },
                  { key: 'value_at_cost', header: 'At cost', type: 'money', total: true }],
                rows: ageing.data.expiring_soon,
              });
            }
            return { filename: `margin-${periodSlug(period)}`, title: 'Margin and ageing', subtitle: periodText(period), sheets };
          }} />
        </div>
      </div>

      <Alert onRetry={margin.reload}>{margin.error}</Alert>

      {margin.loading && !margin.data && <CardSkeleton lines={4} />}
      {margin.data && (
        <>
          <div className="grid kpi" style={{ marginBottom: 16 }}>
            <StatCard label="Revenue" value={inr(margin.data.totals.revenue, 0)} foot="taxable value, this period" />
            <StatCard label="Cost of goods" value={inr(margin.data.totals.cost, 0)} foot="batch purchase rates from Tally" />
            <StatCard label="Margin" value={inr(margin.data.totals.margin, 0)}
              foot={margin.data.totals.margin_pct !== null ? `${margin.data.totals.margin_pct}% of revenue` : ''} />
            <StatCard label="Uncosted lines"
              value={margin.data.rows.filter((r) => !r.costed).length}
              foot="batches bought before the purchase sync"
              tone={margin.data.rows.some((r) => !r.costed) ? 'attention' : undefined} />
          </div>

          <Card title="Where the margin comes from"
            actions={(
              <div className="segmented" role="group" aria-label="Group by">
                {[['item', 'By item'], ['customer', 'By customer'], ['batch', 'By batch']].map(([k, l]) => (
                  <button key={k} aria-pressed={by === k} onClick={() => setBy(k)}>{l}</button>
                ))}
              </div>
            )}>
            {margin.data.rows.length === 0 ? <Empty title="Nothing billed in this period" /> : (
              <>
                <BarsH data={margin.data.rows.slice(0, 6).map((r) => ({
                  label: r.label, value: r.margin, sub: `${r.margin_pct ?? '—'}% of ${inr(r.revenue, 0)}`,
                }))} />
                <div className="table-wrap" style={{ marginTop: 14 }}>
                  <table>
                    <thead><tr><th>{margin.data.by}</th><th className="r">Quantity</th><th className="r">Revenue</th>
                      <th className="r">Cost</th><th className="r">Margin</th><th className="r">%</th></tr></thead>
                    <tbody>
                      {margin.data.rows.map((r) => (
                        <tr key={r.label}>
                          <td>{r.label}{!r.costed && <div className="sub">no purchase rate on record</div>}</td>
                          <td className="r num">{qty(r.qty)}</td>
                          <td className="r num">{inr(r.revenue, 0)}</td>
                          <td className="r num">{inr(r.cost, 0)}</td>
                          <td className="r num"><b>{inr(r.margin, 0)}</b></td>
                          <td className="r num" style={{ color: r.margin_pct < 0 ? 'var(--critical-text)' : undefined }}>
                            {r.margin_pct === null ? '—' : `${r.margin_pct}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-note" style={{ marginTop: 10 }}>{margin.data.note}</div>
              </>
            )}
          </Card>
        </>
      )}

      {ageing.data && (
        <>
          <Card title="How long stock has been sitting" note="By days since purchase, at cost">
            {ageing.data.buckets.length === 0 ? <Empty title="No stock in hand" /> : (
              <StackedBars
                data={ageing.data.buckets.map((b) => ({ label: `${b.bucket} days`, value: b.value, quantity: b.qty }))}
                keys={[{ key: 'value', label: 'Value at cost' }]}
                unit="currency" />
            )}
          </Card>

          <Card title="Running out of time" note="In stock, expiring within sixty days">
            {ageing.data.expiring_soon.length === 0
              ? <Empty title="Nothing expiring soon">Every batch in stock has more than two months on it.</Empty>
              : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Item</th><th>Batch</th><th>Godown</th><th>Expiry</th>
                      <th className="r">Days left</th><th className="r">In stock</th><th className="r">At cost</th></tr></thead>
                    <tbody>
                      {ageing.data.expiring_soon.map((r) => (
                        <tr key={`${r.batch_name}-${r.godown_name}`}>
                          <td>{r.item_name}</td>
                          <td className="mono">{r.batch_name}</td>
                          <td>{r.godown_name}</td>
                          <td>{day(r.expiry_date)}</td>
                          <td className="r num" style={{ color: r.days_to_expiry <= 30 ? 'var(--critical-text)' : 'var(--warning-text)' }}>
                            {r.days_to_expiry}
                          </td>
                          <td className="r num">{qty(r.qty_available)}</td>
                          <td className="r num">{inr(num(r.value_at_cost), 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
        </>
      )}
    </>
  );
}
